/**
 * The jump-in (duel v3 P1.5 d): jump -> air dash -> ท่าโฉบ (dive strike). In a bout, the basic cut (or an attack move) pressed in
 * the air after an air dash becomes a dive: a `DUEL_MOVE.dive.windup` descent to a spot `ahead` of the fighter with its
 * landing spot readable on the ground (`dive(id)` gives the spot and the progress: the shadow is the tell). It lands an
 * attack in the triangle (guardable; the guard reduces it) and, landed, leaves no landing stop. A miss is the long stop.
 * Bout only; outside it the air keeps today's rules.
 */
import { BASIC_CHAIN } from '../world';
import { DUEL_MOVE } from '../data/duelMatch';
import { SKILLS } from '../data/skills';
import type { Entity, PlayerCommand } from '../world';
import { duelMoveOf } from './duelMove';
import { duelRecoveryOf } from './duelRecovery';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelAirSystem>();
export const duelAirOf = (w: object): DuelAirSystem | null => SYSTEMS.get(w) ?? null;

interface Dive {
  t: number;
  z0: number;
  fromX: number;
  fromY: number;
  tx: number;
  ty: number;
}

export interface DiveInfo {
  /** 0 to 1 over the descent. */
  k: number;
  /** The landing spot. */
  x: number;
  y: number;
  /** Starting height (px). */
  z0: number;
}

export class DuelAirSystem implements WorldSystem {
  readonly name = 'duelAir';
  private readonly dives = new Map<number, Dive>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** The dive a fighter is making (null: none). */
  dive(id: number): DiveInfo | null {
    const d = this.dives.get(id);
    if (!d) return null;
    return { k: Math.min(1, d.t / DUEL_MOVE.dive.windup), x: d.tx, y: d.ty, z0: d.z0 };
  }

  refuse(cmd: PlayerCommand): boolean {
    const mv = duelMoveOf(this.h.world);
    const w = this.h.world;
    const p = w.player;
    if (!mv || !mv.inBout(p.id)) return false;
    // A diving fighter takes no commands that move, guard or press a move.
    if (this.dives.has(p.id)) return cmd.t === 'dash' || cmd.t === 'jump' || cmd.t === 'skill' || cmd.t === 'attack' || (cmd.t === 'block' && cmd.on);
    if (cmd.t !== 'attack' && cmd.t !== 'skill') return false;
    if (p.dead || p.stunT > 0 || p.cast || p.z < DUEL_MOVE.dive.minHeight || !p.airDashed) return false;
    if (duelRecoveryOf(w)?.left(p.id)) return false;
    if (cmd.t === 'skill') {
      const id = skillBarOf(w.progress)[cmd.slot];
      const s = id ? SKILLS[id] : undefined;
      if (!s || s.kind !== 'attack' || s.dash) return false;
    }
    const foe = mv.foeOf(p.id);
    if (!foe) return false;
    this.start(p, foe);
    return true;
  }

  private start(p: Entity, foe: Entity): void {
    const fx = foe.x - p.x;
    const fy = foe.y - p.y;
    const fd = Math.hypot(fx, fy) || 1;
    const reach = Math.min(DUEL_MOVE.dive.ahead, Math.max(0, fd - 0.5));
    const tx = p.x + (fx / fd) * reach;
    const ty = p.y + (fy / fd) * reach;
    this.dives.set(p.id, { t: 0, z0: p.z, fromX: p.x, fromY: p.y, tx, ty });
    const win = DUEL_MOVE.dive.windup;
    p.dashT = win;
    p.dashVX = (tx - p.x) / win;
    p.dashVY = (ty - p.y) / win;
    p.vz = 0;
    p.blocking = false;
    p.attackT = Math.max(p.attackT, win);
  }

  step(dt: number): void {
    const w = this.h.world;
    const mv = duelMoveOf(w);
    for (const [id, d] of [...this.dives]) {
      const e = w.get(id);
      const foe = mv?.foeOf(id);
      if (!e || e.dead || e.stunT > 0 || !foe || !mv?.inBout(id)) {
        this.dives.delete(id);
        if (e) e.dashT = 0;
        continue;
      }
      d.t += dt;
      const k = Math.min(1, d.t / DUEL_MOVE.dive.windup);
      e.z = d.z0 * (1 - k);
      e.vz = 0;
      if (k < 1) continue;
      this.dives.delete(id);
      e.z = 0;
      e.dashT = 0;
      e.jumps = 0;
      e.airDashed = false;
      e.swingT = 0.25;
      let landed = false;
      if (Math.hypot(foe.x - e.x, foe.y - e.y) <= DUEL_MOVE.dive.hitR) {
        const power = w.activeArt().basic.power * BASIC_CHAIN[BASIC_CHAIN.length - 1]!.power * DUEL_MOVE.dive.power;
        const out = this.h.hit(e, foe, power, 'attack', 'basic');
        landed = !!out && out !== 'dodge';
      }
      if (landed) mv?.skipLanding.add(id);
      else duelRecoveryOf(w)?.startStop(id, DUEL_MOVE.dive.whiffStop, 'dive-miss');
    }
  }
}
